import { apiService } from './apiService'
import { Garden, GardenFilters, PaginatedResponse, ExportRequest } from '@types'

export const gardenService = {
  // 获取菜园列表
  async getGardens(filters: GardenFilters): Promise<PaginatedResponse<Garden>> {
    const params = new URLSearchParams()

    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status)
    }
    if (filters.farmerId) {
      params.append('farmerId', filters.farmerId)
    }
    if (filters.location) {
      params.append('location', filters.location)
    }
    if (filters.startDate) {
      params.append('startDate', filters.startDate)
    }
    if (filters.endDate) {
      params.append('endDate', filters.endDate)
    }
    if (filters.page) {
      params.append('page', filters.page.toString())
    }
    if (filters.pageSize) {
      params.append('pageSize', filters.pageSize.toString())
    }
    if (filters.search) {
      params.append('search', filters.search)
    }

    return apiService.get<PaginatedResponse<Garden>>(`/gardens?${params.toString()}`)
  },

  // 获取菜园详情
  async getGardenDetail(id: string): Promise<Garden> {
    return apiService.get<Garden>(`/gardens/${id}`)
  },

  // 更新菜园状态
  async updateGardenStatus(id: string, status: 'approved' | 'pending' | 'inactive'): Promise<void> {
    return apiService.put(`/gardens/${id}/status`, { status })
  },

  // 更新菜园信息
  async updateGarden(id: string, data: Partial<Garden>): Promise<Garden> {
    return apiService.put<Garden>(`/gardens/${id}`, data)
  },

  // 获取菜园统计
  async getGardenStatistics(filters: GardenFilters): Promise<any> {
    const params = new URLSearchParams()
    
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status)
    }
    if (filters.farmerId) {
      params.append('farmerId', filters.farmerId)
    }
    if (filters.location) {
      params.append('location', filters.location)
    }
    if (filters.startDate) {
      params.append('startDate', filters.startDate)
    }
    if (filters.endDate) {
      params.append('endDate', filters.endDate)
    }
    
    return apiService.get(`/gardens/statistics?${params.toString()}`)
  },

  // 导出菜园数据（CSV）
  async exportGardens(filters: GardenFilters, format: 'excel' | 'csv' | 'pdf'): Promise<void> {
    const exportRequest: ExportRequest = {
      filters,
      format,
      fields: [
        'id',
        'name',
        'description',
        'location',
        'farmerName',
        'status',
        'createTime',
      ],
    }
    const name = `gardens_${new Date().toISOString().split('T')[0]}.csv`
    return apiService.postDownload('/gardens/export', exportRequest, name)
  },

  // 批量更新菜园状态
  async batchUpdateStatus(ids: string[], status: 'approved' | 'pending' | 'inactive'): Promise<void> {
    return apiService.put('/gardens/batch/status', { ids, status })
  },

  // 创建菜园
  async createGarden(gardenData: Partial<Garden>): Promise<Garden> {
    return apiService.post<Garden>('/gardens', gardenData)
  },

  // 删除菜园
  async deleteGarden(id: string): Promise<void> {
    return apiService.delete(`/gardens/${id}`)
  },

  // 获取菜园增长趋势
  async getGardenGrowthTrend(timeRange: string = '30d'): Promise<any> {
    return apiService.get(`/gardens/growth-trend?range=${timeRange}`)
  },

  // 获取菜园地区分布
  async getGardenRegionDistribution(): Promise<any> {
    return apiService.get('/gardens/region-distribution')
  },

  // 获取菜园营收统计
  async getGardenRevenueStats(): Promise<any> {
    return apiService.get('/gardens/revenue-stats')
  },

  // 获取菜园订阅统计
  async getGardenSubscriptionStats(gardenId: string): Promise<any> {
    return apiService.get(`/gardens/${gardenId}/subscription-stats`)
  },

  // 获取菜园评价分析
  async getGardenRatingAnalysis(gardenId: string): Promise<any> {
    return apiService.get(`/gardens/${gardenId}/rating-analysis`)
  },
}